import { Phone, MessageCircle, FileText } from "lucide-react";
import { LeadFormModal } from "./LeadFormModal";
import { track } from "@/lib/analytics";

/**
 * HubLeadCTA — Compact in-content conversion block for cluster hub pages.
 *
 * Renders a 3-action card (Quote Modal · Call · LINE) intended to sit near
 * the end of a hub's main content, complementing the globally mounted
 * StickyContactBar with an explicit, page-level affordance that AEO and
 * conversion audits can detect statically (LeadFormModal + tel + LINE).
 */

const PHONE_DISPLAY = "083-249-4999";
const PHONE_TEL = "+66832494999";
const LINE_URL = "https://line.me/ti/p/@nycli";

export type HubLeadCTAProps = {
  /** Section heading (TH/EN). */
  title?: string;
  /** Short subtitle / value-prop line. */
  subtitle?: string;
  /** Locale — controls default copy. */
  locale?: "th" | "en";
  /** Service context passed to the lead form (e.g. "NAATI · CCL"). */
  service?: string;
  className?: string;
};

export default function HubLeadCTA({
  title,
  subtitle,
  locale = "th",
  service,
  className = "",
}: HubLeadCTAProps) {
  const t = locale === "en"
    ? {
        title: title ?? "Ready to start? Get a quote in 1 business hour.",
        sub: subtitle ?? "Free consultation · No hidden fees · Lawyer-reviewed quotation.",
        quote: "Request Quote",
        call: "Call now",
        line: "Chat on LINE",
      }
    : {
        title: title ?? "พร้อมเริ่มได้เลย — รับใบเสนอราคาภายใน 1 ชม.ทำการ",
        sub: subtitle ?? "ปรึกษาฟรี · ไม่มีค่าใช้จ่ายแฝง · ใบเสนอราคารับรองโดยทนาย",
        quote: "ขอใบเสนอราคา",
        call: "โทรเลย",
        line: "แชท LINE",
      };

  return (
    <section
      aria-label={locale === "en" ? "Contact NYC Legal" : "ติดต่อ NYC Legal"}
      className={`mx-auto my-12 max-w-5xl px-4 sm:px-6 ${className}`}
    >
      <div className="rounded-3xl border border-amber-200/60 bg-gradient-to-br from-amber-50 via-white to-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-display text-xl font-extrabold leading-snug text-navy-deep sm:text-2xl">
              {t.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-[15px]">{t.sub}</p>
          </div>
          <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:flex-nowrap">
            <LeadFormModal
              defaultService={service}
              trigger={
                <button
                  type="button"
                  onClick={() => track("hub_lead_cta_quote", { service })}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-navy-deep px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-navy-deep/90 lg:flex-none"
                >
                  <FileText className="h-4 w-4" />
                  {t.quote}
                </button>
              }
            />
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={() => track("hub_lead_cta_call", { service })}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-navy-deep hover:border-gold lg:flex-none"
            >
              <Phone className="h-4 w-4" />
              {t.call} {PHONE_DISPLAY}
            </a>
            <a
              href={LINE_URL}
              target="_blank"
              rel="noopener"
              onClick={() => track("hub_lead_cta_line", { service })}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-100 lg:flex-none"
            >
              <MessageCircle className="h-4 w-4" />
              {t.line} @nycli
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
